import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import api from '../lib/api';
import { 
    TrendingUp, 
    Users, 
    Building2, 
    Briefcase,
    Award,
    MapPin,
    Target,
    BarChart3,
    DollarSign,
    Handshake,
    GraduationCap
} from 'lucide-react';

const ImpactDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchImpactData();
    }, []);

    const fetchImpactData = async () => {
        try {
            const response = await api.get('/impact/dashboard');
            setData(response.data);
        } catch (error) {
            console.error('Error fetching impact data:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        if (num === null || num === undefined) return '—';
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num.toLocaleString();
    };

    const formatCurrency = (num) => {
        if (num === null || num === undefined) return '—';
        return `$${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <Skeleton className="h-12 w-64 mb-8" />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        {[...Array(4)].map((_, i) => (
                            <Skeleton key={i} className="h-32" />
                        ))}
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">
                        <Skeleton className="h-80" />
                        <Skeleton className="h-80" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="impact-dashboard">
            {/* Hero Section */}
            <div className="bg-gradient-to-br from-primary/10 via-background to-primary/5 border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-primary rounded-xl">
                            <TrendingUp className="h-8 w-8 text-primary-foreground" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl md:text-4xl font-bold">
                                Impact Dashboard
                            </h1>
                            <p className="text-muted-foreground">
                                Collective impact from HAAPE Member organizations
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Key Metrics Grid — People Trained & Employed */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {/* People Trained (Current Year) */}
                    <Card className="relative overflow-hidden" data-testid="metric-trained-current">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-purple-500/10 to-transparent rounded-bl-full" />
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-2">
                                <GraduationCap className="h-5 w-5" />
                                <span className="text-sm font-medium">Trained This Year</span>
                            </div>
                            <p className="text-4xl font-bold text-purple-600 dark:text-purple-400">
                                {formatNumber(data?.totals?.people_trained_current_year)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                People trained in current year
                            </p>
                        </CardContent>
                    </Card>

                    {/* People Trained (All-Time) */}
                    <Card className="relative overflow-hidden" data-testid="metric-trained-alltime">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-indigo-500/10 to-transparent rounded-bl-full" />
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 mb-2">
                                <Users className="h-5 w-5" />
                                <span className="text-sm font-medium">Trained All-Time</span>
                            </div>
                            <p className="text-4xl font-bold text-indigo-600 dark:text-indigo-400">
                                {formatNumber(data?.totals?.people_trained_all_time)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                Total people trained to date
                            </p>
                        </CardContent>
                    </Card>

                    {/* People Employed (Current Year) */}
                    <Card className="relative overflow-hidden" data-testid="metric-employed-current">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-emerald-500/10 to-transparent rounded-bl-full" />
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 mb-2">
                                <Briefcase className="h-5 w-5" />
                                <span className="text-sm font-medium">Employed This Year</span>
                            </div>
                            <p className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">
                                {formatNumber(data?.totals?.people_employed_current_year)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                People employed in current year
                            </p>
                        </CardContent>
                    </Card>

                    {/* People Employed (All-Time) */}
                    <Card className="relative overflow-hidden" data-testid="metric-employed-alltime">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-500/10 to-transparent rounded-bl-full" />
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-2">
                                <Award className="h-5 w-5" />
                                <span className="text-sm font-medium">Employed All-Time</span>
                            </div>
                            <p className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                                {formatNumber(data?.totals?.people_employed_all_time)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                Total employment placements
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Secondary Metrics Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {/* Employers Engaged */}
                    <Card data-testid="metric-employers-engaged">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Employers Engaged</p>
                                    <p className="text-3xl font-bold">{formatNumber(data?.totals?.employers_engaged)}</p>
                                </div>
                                <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                                    <Handshake className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Average Starting Salary */}
                    <Card data-testid="metric-avg-salary">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Avg Starting Salary</p>
                                    <p className="text-3xl font-bold">{formatCurrency(data?.averages?.avg_starting_salary)}</p>
                                </div>
                                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                                    <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Claimed Organizations */}
                    <Card data-testid="metric-claimed-orgs">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Member Organizations</p>
                                    <p className="text-3xl font-bold">{data?.summary?.total_claimed_orgs || 0}</p>
                                </div>
                                <div className="p-3 bg-primary/10 rounded-full">
                                    <Building2 className="h-6 w-6 text-primary" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* States Covered */}
                    <Card data-testid="metric-states-covered">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">States Covered</p>
                                    <p className="text-3xl font-bold">{data?.states_covered || 0}</p>
                                </div>
                                <div className="p-3 bg-primary/10 rounded-full">
                                    <MapPin className="h-6 w-6 text-primary" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Program Success Rates */}
                {(data?.averages?.completion_rate || data?.averages?.placement_rate) && (
                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                        {data?.averages?.completion_rate && (
                            <Card data-testid="metric-completion-rate">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Target className="h-5 w-5 text-primary" />
                                        Average Completion Rate
                                    </CardTitle>
                                    <CardDescription>
                                        Program completion across reporting organizations
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="flex items-end justify-between">
                                            <span className="text-5xl font-bold text-primary">
                                                {data.averages.completion_rate}%
                                            </span>
                                        </div>
                                        <Progress value={data.averages.completion_rate} className="h-3" />
                                        <p className="text-sm text-muted-foreground">
                                            Percentage of participants who complete their programs
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {data?.averages?.placement_rate && (
                            <Card data-testid="metric-placement-rate">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Briefcase className="h-5 w-5 text-primary" />
                                        Average Job Placement Rate
                                    </CardTitle>
                                    <CardDescription>
                                        Job placement across reporting organizations
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="flex items-end justify-between">
                                            <span className="text-5xl font-bold text-emerald-600 dark:text-emerald-400">
                                                {data.averages.placement_rate}%
                                            </span>
                                        </div>
                                        <Progress value={data.averages.placement_rate} className="h-3 [&>div]:bg-emerald-500" />
                                        <p className="text-sm text-muted-foreground">
                                            Percentage of participants placed in employment
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                )}

                {/* State Breakdown */}
                {data?.by_state?.length > 0 && (
                    <Card className="mb-8" data-testid="impact-by-state">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <MapPin className="h-5 w-5 text-primary" />
                                Impact by State
                            </CardTitle>
                            <CardDescription>
                                Top states by combined training and employment
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {data.by_state.slice(0, 8).map((state) => {
                                    const maxImpact = Math.max(...data.by_state.map(s => s.trained + s.employed));
                                    const stateImpact = state.trained + state.employed;
                                    const percentage = maxImpact > 0 ? (stateImpact / maxImpact) * 100 : 0;
                                    
                                    return (
                                        <div key={state.state} className="space-y-1">
                                            <div className="flex items-center justify-between text-sm">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium">{state.state}</span>
                                                    <Badge variant="outline" className="text-xs">
                                                        {state.organizations} org{state.organizations !== 1 ? 's' : ''}
                                                    </Badge>
                                                </div>
                                                <span className="text-muted-foreground">
                                                    {formatNumber(state.trained)} trained / {formatNumber(state.employed)} employed
                                                </span>
                                            </div>
                                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-gradient-to-r from-primary to-primary/60 rounded-full transition-all duration-500"
                                                    style={{ width: `${percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Data Notice */}
                <div className="mt-8 text-center">
                    <p className="text-sm text-muted-foreground">
                        Data aggregated from {data?.summary?.total_claimed_orgs || 0} HAAPE Member organizations.
                        {data?.summary?.orgs_with_metrics > 0 && (
                            <> {data.summary.orgs_with_metrics} organizations actively reporting metrics.</>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ImpactDashboard;
